import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import kondoLogo from '../assets/Kondify letra negra sin fondo.png';

/**
 * Format currency for display/export
 */
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
    }).format(amount);
};

/**
 * Export data to CSV
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file without extension
 */
export const exportToCSV = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

// Helper to load image
const loadImage = (url) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.src = url;
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null); // Fail gracefully
    });
};

/**
 * Export data to Excel
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file without extension
 * @param {string} sheetName - Name of the sheet
 * @param {string} condominioName - Optional name of the condominium
 */
export const exportToExcel = (data, filename, sheetName = 'Sheet1', condominioName) => {
    const ws = XLSX.utils.json_to_sheet(data, { origin: 'A4' }); // Start data at A4

    // Add Header Manually
    XLSX.utils.sheet_add_aoa(ws, [[condominioName || 'Kondify']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [[`Reporte: ${filename}`]], { origin: 'A2' });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export data to PDF using autoTable
 * @param {string} title - Title of the report
 * @param {Array} columns - Array of column headers
 * @param {Array} rows - Array of arrays containing row data
 * @param {string} filename - Name of the file without extension
 * @param {string} condominioName - Optional name of the condominium
 */
export const exportToPDF = async (title, columns, rows, filename, condominioName) => {
    const doc = new jsPDF();

    // Load Logo (Kondify PNG)
    const logoImg = await loadImage(kondoLogo);

    let startY = 22;

    // Add Logo if exists
    if (logoImg) {
        // Constrain width to 35 and calculate proportional height
        const logoWidth = 35;
        const logoHeight = (logoImg.naturalHeight / logoImg.naturalWidth) * logoWidth;
        
        doc.addImage(logoImg, 'PNG', 14, 10, logoWidth, logoHeight);

        // Move text to the right of the logo (x=55)
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(condominioName || "Kondify", 55, 18); // Dynamic Condominio Name
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        doc.text(title, 55, 26); // Report Title

        // Timestamp
        doc.setFontSize(9);
        doc.setTextColor(150);
        doc.text(`Generado: ${moment().format('DD/MM/YYYY HH:mm')}`, 55, 33);

        startY = 42;
    } else {
        // Fallback no logo
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(condominioName || "Kondify", 14, 20);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(title, 14, 28);
        
        doc.setFontSize(9);
        doc.text(`Generado: ${moment().format('DD/MM/YYYY HH:mm')}`, 14, 35);
        startY = 42;
    }

    autoTable(doc, {
        startY: startY,
        head: [columns],
        body: rows,
        theme: 'grid',
        headStyles: { fillColor: [76, 29, 149], textColor: 255 }, // Violet Kondify brand color
        styles: { fontSize: 8 },
    });

    doc.save(`${filename}.pdf`);
};

/**
 * Helper to flatten object for export if needed
 */
export const flattenData = (data) => {
    return data;
};
